import React, { useContext, useEffect, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import ScreenCard from "../ScreenCard";
import { kycGradient } from "../../configs/linear-gradients";
import { useStatusBarTranslucent } from "../../hooks/use-status-bar-translucent";
import { useIntercom } from "../../hooks/intercom";
import { LiftPerson } from "../../components/LiftPerson";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { TitleMediumText } from "../../components/FormattedText";
import ListGroup from "../../components/ListGroup";
import { useTracking } from "../../hooks/tracking";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { ClientProfile } from "../../__generated__/graphql";

const ClientVerificationPending = () => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { trackEvent } = useTracking();
    const { presentMessageComposer, presentArticleId } = useIntercom();
    const insets = useSafeAreaInsets();
    const currentProfile = useCurrentProfile<ClientProfile>();
    const { openProfileSwitch, profileIds } = useContext(ProfileSwitcherManagerContext);

    const hasMultipleProfiles = useMemo(() => profileIds.length > 1, [profileIds.length]);

    useStatusBarTranslucent();

    // Be careful if you delete this screen move those events to the new screen
    useEffect(() => {
        if (currentProfile && currentProfile.isPrivateClient) {
            void trackEvent("signup_completed_private_client");
        } else if (currentProfile && !currentProfile.isPrivateClient) {
            void trackEvent("signup_completed_client");
        }
    }, [trackEvent, currentProfile]);

    return (
        <ScreenCard
            backgroundGradient={kycGradient}
            buttons={[
                hasMultipleProfiles
                    ? {
                          title: i18n.t("pendingVerification.change_profile"),
                          onPress: openProfileSwitch,
                          mode: "secondary",
                      }
                    : {
                          title: i18n.t("assistance"),
                          onPress: () => {
                              void presentMessageComposer();
                          },
                          size: "medium",
                          mode: "primary",
                      },
            ]}
            contentStyle={{
                paddingTop: insets.top,
                paddingBottom: 0,
            }}
        >
            <VStack v={1} h="2/3" gap={1}>
                <LiftPerson
                    markdownProps={{
                        markdown: i18n.t("screens.auth.clientVerificationPending.description"),
                    }}
                />
                <TitleMediumText>
                    {i18n.t(
                        "screens.auth.clientVerificationPending.in_the_meantime_check_articles",
                    )}
                </TitleMediumText>
            </VStack>
            <>
                <ListGroup
                    borderColor={colors.background}
                    items={[
                        {
                            description: i18n.t(
                                "screens.auth.clientVerificationPending.how_to_create_mission",
                            ),
                            onPress: () => {
                                void presentArticleId("8596965");
                            },
                            value: "",
                        },
                        {
                            description: i18n.t(
                                "screens.auth.clientVerificationPending.how_mission_work_for_clients",
                            ),
                            onPress: () => {
                                void presentArticleId("9983409");
                            },
                            value: "",
                        },
                        {
                            description: i18n.t(
                                "screens.auth.clientVerificationPending.can_transfer_current_employees",
                            ),
                            onPress: () => {
                                void presentArticleId("10265387");
                            },
                            value: "",
                        },
                        {
                            description: i18n.t(
                                "screens.auth.clientVerificationPending.how_payroll_breakdown_works",
                            ),
                            onPress: () => {
                                void presentArticleId("8708309");
                            },
                            value: "",
                        },
                        {
                            description: i18n.t(
                                "screens.auth.clientVerificationPending.how_talent_salary_is_paid",
                            ),
                            onPress: () => {
                                void presentArticleId("9983327");
                            },
                            value: "",
                        },
                    ]}
                />
            </>
        </ScreenCard>
    );
};

export default ClientVerificationPending;
